import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { MonitorQueryKeys } from "./monitorQueryKeys";
import {
  getMonitorErrorDetail,
  getMonitorErrors,
  getSocketHealth,
  MonitorErrorsParams,
} from "@/api/monitor";

// Empty filter arrays are normalized to `undefined` here (not left for each
// caller to remember) so equivalent "no filter" states share one cache entry
// instead of `status: []` and `status: undefined` producing separate keys.
const normalizeParams = (params: MonitorErrorsParams): MonitorErrorsParams => ({
  ...params,
  status: params.status && params.status.length > 0 ? params.status : undefined,
  source: params.source && params.source.length > 0 ? params.source : undefined,
});

// Admin error-monitor list. Polled every 30s so newly-tracked errors show up
// without a manual refresh; server response is itself 60s-cached.
export const useMonitorErrorsPaginated = (params: MonitorErrorsParams) => {
  const normalizedParams = normalizeParams(params);

  return useQuery({
    queryKey: [MonitorQueryKeys.ErrorsPaginated, normalizedParams],
    placeholderData: keepPreviousData,
    refetchInterval: 30_000,
    queryFn: ({ signal }) =>
      getMonitorErrors({ signal, params: normalizedParams }),
  });
};

// Single error-group detail (drill-down panel). `fingerprint` is null until
// the admin selects a row; `enabled` keeps the query from firing early.
export const useMonitorErrorDetail = (fingerprint: string | null) => {
  return useQuery({
    queryKey: [MonitorQueryKeys.ErrorDetail, fingerprint],
    queryFn: ({ signal }) =>
      getMonitorErrorDetail({
        signal,
        params: { fingerprint: fingerprint as string },
      }),
    enabled: fingerprint !== null,
  });
};

// Per-hour anomalous socket disconnect-reason counters for the monitor page.
export const useSocketHealth = (hours: number) => {
  return useQuery({
    queryKey: [MonitorQueryKeys.SocketHealth, hours],
    refetchInterval: 60_000,
    queryFn: ({ signal }) => getSocketHealth({ signal, params: { hours } }),
  });
};
